import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNotify, CREATE } from 'react-admin';
import { FormSpy } from 'react-final-form';
import {
  Grid,
  Typography,
  makeStyles,
  CircularProgress
} from '@material-ui/core';

import { DistributionListContext } from './DistributionListContext';
import DistributionListApi from '../../../core/apis/DistributionListApi';
import { useFetch } from '../../../core/hooks/UseFetch';
import SummaryGrid from './SummaryGrid';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import { splitAndTrimString } from '../../../core/utils/index';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const useStyles = makeStyles({
  header: {
    paddingTop: '0px',
    fontSize: 20,
    fontWeight: 600
  },
  description: {
    display: 'flex',
    marginTop: '7px',
    fontSize: 14
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center'
  }
});

/*
 * Form level validation.
 * Verify that recipients list has one item at least
 */
const validateRecipients = values => {
  const errors = {};
  if (!values.recipients || values.recipients.length < 1) {
    errors.recipients = 'At least one recipient must be selected.';
  }
  return errors;
};

const SummaryStep = props => {
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const { dlContext, setDlContext } = useContext(DistributionListContext);
  const classes = useStyles();

  const { variant, onSubmitEnd, permissions = [], userProfile } = props;
  const { distributionList = {}, options } = dlContext;
  const [filteredData, setFilteredData] = useState([]);
  const [nameFilter, setNameFilter] = useState();
  const [emailFilter, setEmailFilter] = useState();
  const [fundFilter, setFundFilter] = useState();
  const [investorFilter, setInvestorFilter] = useState();

  const { data = [], loading } = useFetch(
    DistributionListApi.getEmailAddressesByFilter,
    options
  );
  const [finalData, setFinalData] = useState([]);

  const { hasComposeEmailDLPermission, hasExportPermission } = useMemo(() => {
    return {
      hasComposeEmailDLPermission: permissions.includes(
        'AUTH_COMPOSE_EMAIL_DL'
      ),
      hasExportPermission: permissions.includes('AUTH_EXPORT_EXCEL')
    };
  }, [permissions]);

  const { initRecipients = [] } = useMemo(() => {
    const initRecipients = [];

    setFilteredData(data);
    setFinalData(data);

    if (data) {
      data.forEach(obj => {
        initRecipients.push({
          userId: obj.userId,
          fund: obj.fundId,
          email: obj.email,
          ccEmails: obj.ccEmails,
          includeAllCcEmails: obj.ccEmails.length > 0
        });
      });
    }
    return { initRecipients };
  }, [data]);

  const addExcludedContacts = excludedContacts => {
    if (excludedContacts) {
      let newArray = [];
      excludedContacts.forEach(item => {
        let userType;
        if (item.userAdmingFlag && item.fundAdminFlag) userType = 'SUPER_ADMIN';
        else if (!item.userAdminFlag && item.fundAdminFlag)
          userType = 'FUND_ADMIN';
        else if (!item.userAdminFlag && !item.fundAdminFlag)
          userType = 'REGULAR_CONTACT';
        let newValue = {
          ccEmails: item.ccEmails,
          email: item.email,
          firstName: item.firstName,
          fundId: item.fundId,
          fundName: item.fundName,
          groups: item.groups,
          investors: item.investors,
          lastName: item.lastName,
          userId: item.userId,
          userType: userType
        };
        newArray.push(newValue);
      });
      let newValue = finalData.concat(newArray);
      setFinalData(newValue);
    }
  };

  const handleCreateDistributionList = async values => {
    const emailFundRecipientList = [...values.recipients];
    emailFundRecipientList.forEach(recipient => {
      delete recipient.userId;
      delete recipient.ccEmails;
    });
    const body = {
      name: distributionList.name,
      emailTemplateId: distributionList.templateId,
      emailFundRecipientList: [...emailFundRecipientList]
    };

    try {
      await dataProvider(CREATE, 'distributionlists', { data: body });
    } catch (error) {
      notifyServerError(error);
    } finally {
      notify('ra.notification.created');
      onSubmitEnd();
    }
  };

  const filterByEmail = event => {
    setEmailFilter(cleanInputFilter(event.target.value));
  };

  const filterByName = event => {
    setNameFilter(cleanInputFilter(event.target.value));
  };

  const filterByFund = event => {
    setFundFilter(cleanInputFilter(event.target.value));
  };

  const filterByInvestor = event => {
    setInvestorFilter(cleanInputFilter(event.target.value));
  };

  const cleanInputFilter = filterText => {
    return filterText.trim().toLowerCase();
  };

  useEffect(() => {
    if (finalData) {
      let dataToFilter = finalData;

      const contactNameList = splitAndTrimString(nameFilter, ',');
      const emailList = splitAndTrimString(emailFilter, ',');
      const investorList = splitAndTrimString(investorFilter, ',');
      const fundList = splitAndTrimString(fundFilter, ',');

      setFilteredData(
        dataToFilter
          .filter(obj =>
            contactNameList
              ? contactNameList.some(name =>
                  obj.firstName
                    .concat(' ' + obj.lastName)
                    .toLowerCase()
                    .includes(name === '' ? null : name)
                )
              : true
          )
          .filter(obj =>
            emailList
              ? emailList.some(email =>
                  obj.email.toLowerCase().includes(email === '' ? null : email)
                )
              : true
          )
          .filter(obj =>
            fundList
              ? fundList.some(fund =>
                  obj.fundName.toLowerCase().includes(fund === '' ? null : fund)
                )
              : true
          )
          .filter(obj =>
            investorList
              ? investorList.some(investorNameFilter =>
                  obj.investors.some(investor =>
                    investor.name
                      .toLowerCase()
                      .includes(
                        investorNameFilter === '' ? null : investorNameFilter
                      )
                  )
                )
              : true
          )
      );
    }
  }, [nameFilter, investorFilter, fundFilter, emailFilter, finalData]);

  const populateRecipients = () => {
    if (finalData && initRecipients.length < finalData.length) {
      let newRecipients = [];
      finalData.forEach(item => {
        newRecipients.push({
          userId: item.userId,
          fund: item.fundId,
          email: item.email,
          ccEmails: item.ccEmails,
          includeAllCcEmails: item.ccEmails.length > 0
        });
      });
      return newRecipients;
    }
    return initRecipients;
  };

  return (
    <CustomSimpleForm
      save={handleCreateDistributionList}
      toolbar={null}
      resource={props.resource}
      variant={variant}
      margin="normal"
      initialValues={{ recipients: populateRecipients() }}
      validate={validateRecipients}
    >
      <FormSpy subscription={{ pristine: true, values: true }}>
        {formprops => {
          // Store current form API object
          if (dlContext.currentForm === undefined) {
            setDlContext({ ...dlContext, currentForm: formprops.form });
          }

          return (
            <Grid container spacing={2}>
              <Grid item>
                <Typography variant="h5" className={classes.header}>
                  Summary
                </Typography>
                <span className={classes.description}>
                  Review the results and save the Distribution List
                </span>
              </Grid>
              <Grid item xs={12}>
                {loading ? (
                  <div className={classes.loaderContainer}>
                    <CircularProgress />
                  </div>
                ) : (
                  <SummaryGrid
                    choices={filteredData}
                    formprops={formprops}
                    distributionList={distributionList}
                    dlOptions={options}
                    hasComposeEmailDLPermission={hasComposeEmailDLPermission}
                    hasExportPermission={hasExportPermission}
                    userProfile={userProfile}
                    filterByName={filterByName}
                    filterByEmail={filterByEmail}
                    filterByFund={filterByFund}
                    filterByInvestor={filterByInvestor}
                    addExcludedContacts={addExcludedContacts}
                    finalData={finalData}
                  />
                )}
              </Grid>
            </Grid>
          );
        }}
      </FormSpy>
    </CustomSimpleForm>
  );
};

export default SummaryStep;
